import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
  useMapEvents,
  CircleMarker,
  LayersControl,
} from 'react-leaflet';
import L from 'leaflet';
// Imported here rather than relying on another admin page having pulled it in.
import 'leaflet/dist/leaflet.css';
import { useSimulationStore } from '../../stores/simulationStore';
import { SHIP_TYPE_COLORS } from '../Config/AisTargetPanel';
import type { AisTargetState } from '../../types';
import { FrontDoorTileLayer } from '../../../../components/Map/FrontDoorTileLayer';

const { BaseLayer, Overlay } = LayersControl;

// Icon caches to avoid recreating Leaflet icons on every render
const vesselIconCache = new Map<number, L.DivIcon>();
const aisIconCache = new Map<string, L.DivIcon>();

// Custom vessel icon (cached by rounded heading)
const createVesselIcon = (heading: number) => {
  const key = Math.round(heading);
  let icon = vesselIconCache.get(key);
  if (!icon) {
    icon = L.divIcon({
      className: 'vessel-marker',
      html: `
        <svg viewBox="0 0 24 24" style="transform: rotate(${heading}deg)">
          <path fill="#0ea5e9" d="M12 2L6 22h12L12 2z"/>
          <path fill="#0284c7" d="M12 2L6 22h6V2z"/>
        </svg>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
    vesselIconCache.set(key, icon);
    // Keep cache bounded
    if (vesselIconCache.size > 360) vesselIconCache.clear();
  }
  return icon;
};

// AIS target icon (cached by rounded heading + ship type)
const createAisIcon = (heading: number, shipType: string) => {
  const key = `${Math.round(heading)}_${shipType}`;
  let icon = aisIconCache.get(key);
  if (!icon) {
    const color = SHIP_TYPE_COLORS[shipType] || '#9ca3af';
    icon = L.divIcon({
      className: 'ais-marker',
      html: `
        <svg viewBox="0 0 20 20" style="transform: rotate(${heading}deg)">
          <polygon points="10,2 3,18 17,18" fill="${color}" stroke="white" stroke-width="1"/>
        </svg>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    aisIconCache.set(key, icon);
    if (aisIconCache.size > 1000) aisIconCache.clear();
  }
  return icon;
};

// AIS targets layer
function AisTargetsLayer() {
  const aisTargets = useSimulationStore((s) => s.aisTargets);

  return (
    <>
      {aisTargets.map((target: AisTargetState) => {
        const color = SHIP_TYPE_COLORS[target.config.ship_type] || '#9ca3af';
        return (
          <React.Fragment key={target.config.mmsi}>
            {/* Route line (dashed) */}
            <Polyline
              positions={[
                [target.config.start_lat, target.config.start_lon],
                [target.config.end_lat, target.config.end_lon],
              ]}
              pathOptions={{ color, weight: 1, dashArray: '4, 4', opacity: 0.5 }}
            />
            {/* Start marker */}
            <CircleMarker
              center={[target.config.start_lat, target.config.start_lon]}
              radius={4}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.6, weight: 1 }}
            />
            {/* End marker */}
            <CircleMarker
              center={[target.config.end_lat, target.config.end_lon]}
              radius={4}
              pathOptions={{ color, fillColor: 'white', fillOpacity: 0.8, weight: 2 }}
            />
            {/* Vessel marker */}
            <Marker
              position={[target.latitude, target.longitude]}
              icon={createAisIcon(target.heading, target.config.ship_type)}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

// Show markers for AIS start/end points being edited (before target is submitted)
function AisEditingLayer() {
  const aisEditingTarget = useSimulationStore((s) => s.aisEditingTarget);

  if (!aisEditingTarget) return null;

  const hasStart = aisEditingTarget.start_lat !== undefined && aisEditingTarget.start_lat !== 0;
  const hasEnd = aisEditingTarget.end_lat !== undefined && aisEditingTarget.end_lat !== 0;

  return (
    <>
      {hasStart && (
        <CircleMarker
          center={[aisEditingTarget.start_lat!, aisEditingTarget.start_lon!]}
          radius={7}
          pathOptions={{
            color: '#ef4444',
            fillColor: '#ef4444',
            fillOpacity: 0.8,
            weight: 2,
          }}
        />
      )}
      {hasEnd && (
        <CircleMarker
          center={[aisEditingTarget.end_lat!, aisEditingTarget.end_lon!]}
          radius={7}
          pathOptions={{
            color: '#22c55e',
            fillColor: '#22c55e',
            fillOpacity: 0.8,
            weight: 2,
          }}
        />
      )}
      {hasStart && hasEnd && (
        <Polyline
          positions={[
            [aisEditingTarget.start_lat!, aisEditingTarget.start_lon!],
            [aisEditingTarget.end_lat!, aisEditingTarget.end_lon!],
          ]}
          pathOptions={{ color: '#f59e0b', weight: 2, dashArray: '6, 6', opacity: 0.7 }}
        />
      )}
    </>
  );
}

// Route creation state - shared between components
interface RouteCreationState {
  waypoints: [number, number][];
  isCreating: boolean;
}

// Map click handler component for multi-waypoint routes and AIS point setting
function MapClickHandler({
  routeCreation,
  setRouteCreation,
}: {
  routeCreation: RouteCreationState;
  setRouteCreation: React.Dispatch<React.SetStateAction<RouteCreationState>>;
}) {
  const { mapMode, setMapMode, aisEditingTarget, setAisEditingTarget } = useSimulationStore();

  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;

      // AIS start point
      if (mapMode === 'aisStart') {
        setAisEditingTarget({ ...aisEditingTarget, start_lat: lat, start_lon: lng });
        setMapMode('pan');
        return;
      }

      // AIS end point
      if (mapMode === 'aisEnd') {
        setAisEditingTarget({ ...aisEditingTarget, end_lat: lat, end_lon: lng });
        setMapMode('pan');
        return;
      }

      if (!routeCreation.isCreating) return;

      setRouteCreation((prev) => ({
        ...prev,
        waypoints: [...prev.waypoints, [lat, lng]],
      }));
    },
  });

  // Render waypoints being placed
  return (
    <>
      {routeCreation.waypoints.map((wp, i) => (
        <CircleMarker
          key={i}
          center={wp}
          radius={8}
          pathOptions={{
            color: i === 0 ? '#ef4444' : '#f59e0b',
            fillColor: i === 0 ? '#ef4444' : '#f59e0b',
            fillOpacity: 1,
          }}
        />
      ))}
      {/* Show lines between waypoints being placed */}
      {routeCreation.waypoints.length >= 2 && (
        <Polyline
          positions={routeCreation.waypoints}
          pathOptions={{ color: '#f59e0b', weight: 2, dashArray: '5, 5' }}
        />
      )}
    </>
  );
}

// Auto-center on vessel and persist map state
function VesselTracker() {
  const map = useMap();
  const setMapState = useSimulationStore((s) => s.setMapState);
  const vesselLat = useSimulationStore((s) => s.state?.vessel?.latitude);
  const vesselLng = useSimulationStore((s) => s.state?.vessel?.longitude);
  const simStatus = useSimulationStore((s) => s.state?.status);
  const [autoCenter, setAutoCenter] = useState(true);
  const initializedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Restore saved map position on first render
  useEffect(() => {
    if (!initializedRef.current) {
      const { mapState } = useSimulationStore.getState();
      if (mapState) {
        map.setView(mapState.center, mapState.zoom, { animate: false });
      }
      initializedRef.current = true;
    }
  }, [map]);

  // Prevent trackpad pinch-to-zoom from triggering browser zoom.
  // Browsers fire wheel events with ctrlKey=true for trackpad pinch gestures.
  useEffect(() => {
    const container = map.getContainer();
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [map]);

  useEffect(() => {
    if (autoCenter && vesselLat != null && vesselLng != null && simStatus === 'running') {
      map.setView([vesselLat, vesselLng], map.getZoom(), {
        animate: true,
        duration: 0.5,
      });
    }
  }, [vesselLat, vesselLng, autoCenter, map, simStatus]);

  // Debounced save to avoid thrashing store/localStorage on every frame
  const debouncedSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      setMapState({ center: [center.lat, center.lng], zoom });
    }, 500);
  }, [map, setMapState]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Disable auto-center when user pans, debounce map state saves
  useMapEvents({
    dragstart: () => setAutoCenter(false),
    moveend: debouncedSave,
    zoomend: debouncedSave,
  });

  return null;
}

// Map toolbar component
function MapToolbar({
  routeCreation,
  onStartRoute,
  onFinishRoute,
  onCancelRoute,
  onUndoWaypoint,
}: {
  routeCreation: RouteCreationState;
  onStartRoute: () => void;
  onFinishRoute: () => void;
  onCancelRoute: () => void;
  onUndoWaypoint: () => void;
}) {
  const route = useSimulationStore((s) => s.route);
  const clearRoute = useSimulationStore((s) => s.clearRoute);
  const repeatRoute = useSimulationStore((s) => s.repeatRoute);
  const toggleRepeatRoute = useSimulationStore((s) => s.toggleRepeatRoute);


  // If creating a route, show route creation controls
  if (routeCreation.isCreating) {
    return (
      <div className="absolute top-4 left-4 z-[1000] bg-nautical-panel rounded-lg p-2 border border-nautical-border">
        <div className="flex flex-col space-y-2">
          {/* Instructions */}
          <div className="text-sm text-gray-300 px-2">
            {routeCreation.waypoints.length === 0 ? (
              <span className="text-yellow-400">Click to set start point</span>
            ) : routeCreation.waypoints.length === 1 ? (
              <span className="text-yellow-400">Click to add waypoints</span>
            ) : (
              <span className="text-green-400">
                {routeCreation.waypoints.length} waypoints - click to add more
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex space-x-1">
            <button
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                routeCreation.waypoints.length >= 2
                  ? 'bg-green-600 text-white hover:bg-green-500'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
              onClick={onFinishRoute}
              disabled={routeCreation.waypoints.length < 2}
              title="Finish route (Enter)"
            >
              Finish Route
            </button>
            <button
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                routeCreation.waypoints.length > 0
                  ? 'bg-yellow-600 text-white hover:bg-yellow-500'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
              onClick={onUndoWaypoint}
              disabled={routeCreation.waypoints.length === 0}
              title="Remove last waypoint (Backspace)"
            >
              Undo
            </button>
            <button
              className="px-3 py-2 rounded-md text-sm text-gray-300 hover:bg-nautical-dark transition-colors"
              onClick={onCancelRoute}
              title="Cancel (Escape)"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal toolbar
  return (
    <div className="absolute top-4 left-20 z-[1000] flex space-x-1 bg-nautical-panel rounded-lg p-1 border border-nautical-border">
      <button
        className="px-3 py-2 rounded-md text-sm text-white bg-marine-600 hover:bg-marine-500 transition-colors"
        onClick={onStartRoute}
        title="Create a new route"
      >
        New Route
      </button>
      {route && (
        <button
          className="px-3 py-2 rounded-md text-sm text-gray-300 hover:bg-red-600 hover:text-white transition-colors"
          onClick={clearRoute}
          title="Clear current route"
        >
          Clear Route
        </button>
      )}
      {route && (
        <button
          className={`px-3 py-2 rounded-md text-sm transition-colors flex items-center space-x-1 ${
            repeatRoute
              ? 'bg-marine-600 text-white'
              : 'text-gray-300 hover:bg-nautical-dark'
          }`}
          onClick={toggleRepeatRoute}
          title={repeatRoute ? 'Repeat route is ON - click to disable' : 'Repeat route when complete'}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 1l4 4-4 4" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <path d="M7 23l-4-4 4-4" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
          <span>Repeat</span>
        </button>
      )}
    </div>
  );
}

// Route info overlay
function RouteInfo() {
  const route = useSimulationStore((s) => s.route);
  const routeState = useSimulationStore((s) => s.state?.route_state);

  if (!route) return null;

  return (
    <div className="absolute bottom-4 right-4 z-[1000] bg-nautical-panel rounded-lg p-3 border border-nautical-border text-sm">
      <div className="text-gray-400 text-xs uppercase mb-2">Route</div>
      <div className="space-y-1">
        <div>
          <span className="text-gray-400">Distance:</span>{' '}
          <span className="text-gray-100 font-mono">
            {route.distance_nm.toFixed(1)} nm
          </span>
        </div>
        {route.waypoints && route.waypoints.length > 2 && (
          <div>
            <span className="text-gray-400">Waypoints:</span>{' '}
            <span className="text-gray-100 font-mono">
              {route.waypoints.length}
            </span>
          </div>
        )}
        {routeState && (
          <>
            <div>
              <span className="text-gray-400">Leg:</span>{' '}
              <span className="text-gray-100 font-mono">
                {routeState.current_leg_index + 1} / {routeState.total_legs}
              </span>
            </div>
            <div>
              <span className="text-gray-400">To WP:</span>{' '}
              <span className="text-gray-100 font-mono">
                {routeState.distance_to_waypoint.toFixed(2)} nm
              </span>
            </div>
            <div>
              <span className="text-gray-400">Bearing:</span>{' '}
              <span className="text-gray-100 font-mono">
                {routeState.current_bearing.toFixed(0)}°
              </span>
            </div>
            {routeState.is_turning && (
              <div className="text-yellow-400">
                Turning {routeState.turn_rate > 0 ? 'Stbd' : 'Port'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Manages map cursor based on current interaction mode
function CursorManager({ isCreatingRoute }: { isCreatingRoute: boolean }) {
  const map = useMap();
  const mapMode = useSimulationStore((s) => s.mapMode);

  useEffect(() => {
    const container = map.getContainer();
    const isCrosshair = isCreatingRoute || mapMode === 'aisStart' || mapMode === 'aisEnd';
    if (isCrosshair) {
      container.classList.add('crosshair-mode');
    } else {
      container.classList.remove('crosshair-mode');
    }
    return () => {
      container.classList.remove('crosshair-mode');
    };
  }, [map, isCreatingRoute, mapMode]);

  return null;
}

export default function MapView() {
  const state = useSimulationStore((s) => s.state);
  const route = useSimulationStore((s) => s.route);
  const mapState = useSimulationStore((s) => s.mapState);
  const setRouteWaypoints = useSimulationStore((s) => s.setRouteWaypoints);
  const clearRoute = useSimulationStore((s) => s.clearRoute);
  const mapRef = useRef<L.Map>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Route creation state
  const [routeCreation, setRouteCreation] = useState<RouteCreationState>({
    waypoints: [],
    isCreating: false,
  });

  // Start new route - clear existing and enter creation mode
  const handleStartRoute = useCallback(() => {
    clearRoute();
    setRouteCreation({
      waypoints: [],
      isCreating: true,
    });
  }, [clearRoute]);

  // Finish route - save waypoints (await to ensure route is set before exiting creation mode)
  const handleFinishRoute = useCallback(async () => {
    if (routeCreation.waypoints.length < 2) return;

    try {
      await setRouteWaypoints(
        routeCreation.waypoints.map(([latitude, longitude], i) => ({
          latitude,
          longitude,
          name:
            i === 0
              ? 'Start'
              : i === routeCreation.waypoints.length - 1
                ? 'End'
                : `WP${i}`,
        }))
      );
      setRouteCreation({ waypoints: [], isCreating: false });
    } catch (err) {
      console.error('Failed to save route:', err);
      // Keep waypoints so user can retry — don't clear the form
    }
  }, [routeCreation.waypoints, setRouteWaypoints]);

  // Cancel route creation
  const handleCancelRoute = useCallback(() => {
    setRouteCreation({ waypoints: [], isCreating: false });
  }, []);

  // Undo last waypoint
  const handleUndoWaypoint = useCallback(() => {
    setRouteCreation((prev) => ({
      ...prev,
      waypoints: prev.waypoints.slice(0, -1),
    }));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!routeCreation.isCreating) return;

      if (e.key === 'Escape') {
        handleCancelRoute();
      } else if (e.key === 'Enter' && routeCreation.waypoints.length >= 2) {
        handleFinishRoute();
      } else if (e.key === 'Backspace' && routeCreation.waypoints.length > 0) {
        e.preventDefault();
        handleUndoWaypoint();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [routeCreation.isCreating, routeCreation.waypoints.length, handleCancelRoute, handleFinishRoute, handleUndoWaypoint]);

  return (
    <div className="w-full h-full relative" ref={containerRef}>
      <MapContainer
        center={mapState.center}
        zoom={mapState.zoom}
        className="w-full h-full"
        ref={mapRef}
      >
        <LayersControl position="topright">
          {/* Google Maps Layers */}
          <BaseLayer name="Google Satellite">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              attribution='&copy; Google Maps'
              maxZoom={20}
            />
          </BaseLayer>
          <BaseLayer checked name="Google Hybrid">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
              attribution='&copy; Google Maps'
              maxZoom={20}
            />
          </BaseLayer>
          <BaseLayer name="Google Terrain">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}"
              attribution='&copy; Google Maps'
              maxZoom={20}
            />
          </BaseLayer>
          <BaseLayer name="Google Roads">
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              attribution='&copy; Google Maps'
              maxZoom={20}
            />
          </BaseLayer>
          <BaseLayer name="OpenStreetMap">
            <FrontDoorTileLayer
              source="osm"
              fallbackUrl="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
          </BaseLayer>

          {/* Nautical Chart Overlay */}
          <Overlay checked name="Sea Marks">
            <TileLayer
              url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
              attribution='&copy; <a href="http://www.openseamap.org">OpenSeaMap</a>'
            />
          </Overlay>
        </LayersControl>

        {/* Existing route (only show when not creating) */}
        {!routeCreation.isCreating && route && route.waypoints && route.waypoints.length >= 2 && (
          <>
            {/* Route polyline through all waypoints */}
            <Polyline
              positions={route.waypoints.map((wp) => [wp.latitude, wp.longitude])}
              pathOptions={{
                color: '#0ea5e9',
                weight: 2,
                dashArray: '10, 10',
              }}
            />
            {/* Waypoint markers */}
            {route.waypoints.map((wp, i) => (
              <CircleMarker
                key={i}
                center={[wp.latitude, wp.longitude]}
                radius={i === 0 ? 8 : i === route.waypoints.length - 1 ? 8 : 6}
                pathOptions={{
                  color: i === 0 ? '#ef4444' : i === route.waypoints.length - 1 ? '#22c55e' : '#f59e0b',
                  fillColor: i === 0 ? '#ef4444' : i === route.waypoints.length - 1 ? '#22c55e' : '#f59e0b',
                  fillOpacity: 1,
                }}
              />
            ))}
          </>
        )}

        {/* Vessel marker */}
        {state?.vessel && (
          <Marker
            position={[state.vessel.latitude, state.vessel.longitude]}
            icon={createVesselIcon(state.vessel.heading)}
          />
        )}

        {/* Track history line */}
        {!routeCreation.isCreating && state?.vessel && route && state.vessel.distance_traveled > 0 && (
          <Polyline
            positions={[
              [route.start_lat, route.start_lon],
              [state.vessel.latitude, state.vessel.longitude],
            ]}
            pathOptions={{
              color: '#6366f1',
              weight: 2,
              opacity: 0.6,
            }}
          />
        )}

        {/* AIS targets */}
        <AisTargetsLayer />
        <AisEditingLayer />

        <MapClickHandler
          routeCreation={routeCreation}
          setRouteCreation={setRouteCreation}
        />
        <CursorManager isCreatingRoute={routeCreation.isCreating} />
        <VesselTracker />
      </MapContainer>

      <MapToolbar
        routeCreation={routeCreation}
        onStartRoute={handleStartRoute}
        onFinishRoute={handleFinishRoute}
        onCancelRoute={handleCancelRoute}
        onUndoWaypoint={handleUndoWaypoint}
      />
      {!routeCreation.isCreating && <RouteInfo />}
    </div>
  );
}
